import type React from 'react';
import { useCallback, useState, useMemo } from 'react';
import { useGetProjects, useCreateProject } from '../../hooks/useProjects';
import { ProjectGridItem } from '@shadeables/ui';
import { useNavigate } from 'react-router-dom';
import LoggedInContentWrapper from '../../components/LoggedInContentWrapper';
import { CreateProjectModal, UpsellModal } from '../../components/modals';
import styles from './Dashboard.module.css';
import { useCanAccess, useBilling } from '@shadeables/billing';
import type { FireShaderConfig } from '@shadeables/shadermodel';

const Dashboard: React.FC = () => {
  const { data: projects, isLoading, error } = useGetProjects();

  const createProject = useCreateProject();
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isUpsellModalOpen, setIsUpsellModalOpen] = useState(false);
  const { canCreateProject } = useCanAccess();
  const { plan } = useBilling();
  const navigate = useNavigate();

  const handleCreateProject = useCallback(
    (name: string) => {
      // Default to fire shader for new projects
      const defaultConfig: FireShaderConfig = {
        name: 'fire',
        duration: 3000,
        volatility: 2.0,
        baseColor: '#ff6b35',
        topColor: '#ffc649',
        backgroundColor: '#000000',
        width: 512,
        height: 512,
        density: 1.0,
      };

      createProject.mutate(
        {
          name,
          config: defaultConfig,
        },
        {
          onSuccess: () => {
            setIsCreateModalOpen(false);
          },
        }
      );
    },
    [createProject]
  );

  const handleAddProjectClick = useCallback(() => {
    const currentProjectCount = projects?.length || 0;
    if (canCreateProject(currentProjectCount)) {
      setIsCreateModalOpen(true);
    } else {
      setIsUpsellModalOpen(true);
    }
  }, [canCreateProject, projects?.length]);

  const handleCloseCreateModal = useCallback(() => {
    setIsCreateModalOpen(false);
  }, []);

  const handleCloseUpsellModal = useCallback(() => {
    setIsUpsellModalOpen(false);
  }, []);

  // Memoize the project navigation handler
  const handleProjectClick = useCallback(
    (projectId: string) => {
      navigate(`/project/${projectId}`);
    },
    [navigate]
  );

  // Memoize skeleton items array
  const skeletonItems = useMemo(
    () =>
      Array.from({ length: 4 }).map((_, index) => (
        <ProjectGridItem key={index} loading onClick={() => {}} />
      )),
    []
  );

  const currentProjectCount = projects?.length || 0;
  const hasReachedLimit = !canCreateProject(currentProjectCount);

  return (
    <LoggedInContentWrapper title="Dashboard">
      <div className={styles.content}>
        <div className={styles.projectGrid}>
          {isLoading ? (
            /* Show 4 skeleton items while loading */
            skeletonItems
          ) : error ? (
            <div className={styles.error}>Error loading projects</div>
          ) : (
            <>
              <ProjectGridItem isAddButton onClick={handleAddProjectClick} />
              {projects?.map((project) => (
                <ProjectGridItem
                  key={project.id}
                  name={project.name}
                  createdAt={project.created_at}
                  onClick={() => handleProjectClick(project.id)}
                />
              ))}
            </>
          )}
        </div>
        {!isLoading && !error && plan === 'free' && hasReachedLimit && (
          <p className={styles.warning}>
            You have reached your project limit. Upgrade to create more
            projects.
          </p>
        )}

        <CreateProjectModal
          isOpen={isCreateModalOpen}
          onClose={handleCloseCreateModal}
          onSubmit={handleCreateProject}
          isLoading={createProject.isPending}
        />

        <UpsellModal
          isOpen={isUpsellModalOpen}
          onClose={handleCloseUpsellModal}
        />
      </div>
    </LoggedInContentWrapper>
  );
};

export default Dashboard;
